import React, { useEffect, useMemo } from "react";
import { Button, Space, Upload } from "antd";
import { isNil, size, truncate } from "lodash";
import { aiccCanDoStatementApi } from "services/aiccLearningAssets";
import {
  AiccCanDoStatement,
  AiccCanDoStatementPayload,
  AiccCanDoStatementUploadRow,
} from "types/model/aiccLearningAssets";
import AgenticAssetLink from "../AgenticAssetLink";
import LearningAssetPage from "../LearningAssetPage";
import { useAssetCrud } from "../hooks/useAssetCrud";
import { useAssetImport } from "../hooks/useAssetImport";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";
import UploadPreviewModal from "../UploadPreviewModal";
import CanDoStatementModal from "./CanDoStatementModal";
import getColumns, { previewColumns } from "./columnConfig";

const CAN_DO_STATEMENT_TITLE = "Can-do Statement";

const CAN_DO_STATEMENT_INTRO =
  'Skill-based milestones that track what you can actually do in the language (e.g., "Can order food at a ' +
  'restaurant", "Can ask for directions"). They\'re evaluated after each conversation to show your real-world ' +
  "progress.";

const FILE_ACCEPT = ".zip";

const LABEL_MAX_LENGTH = 40;

const getLabel = ({ statement }: AiccCanDoStatement | AiccCanDoStatementPayload) =>
  truncate(statement, { length: LABEL_MAX_LENGTH });

const EMPTY_ROWS: AiccCanDoStatementUploadRow[] = [];

function CanDoStatementManagement() {
  const state = useLearningAssetVersions(aiccCanDoStatementApi.fetchVersions);
  const { languageId, groupStatus, isEditable, isDraft, canManage, canDelete, isSaving, runWrite } = state;

  const { isModalVisible, editingItem, openModal, closeModal, handleDelete, handleSubmit } = useAssetCrud<
    AiccCanDoStatement,
    AiccCanDoStatementPayload
  >({ api: aiccCanDoStatementApi, runWrite, getLabel });

  const { uploadFile, preview, isValidating, closeUpload, selectFile, confirmUpload } = useAssetImport({
    api: aiccCanDoStatementApi,
    languageId,
    isDraft,
    runWrite,
  });

  const isUploading = isValidating || !isNil(preview);
  const isActionDisabled = isNil(languageId) || isUploading;
  const previewRows = preview?.rows ?? EMPTY_ROWS;

  useEffect(() => {
    closeModal();
  }, [closeModal, groupStatus, languageId]);

  const columns = useMemo(
    () =>
      getColumns({ canManage, canDelete, isActionDisabled: isUploading, onEdit: openModal, onDelete: handleDelete }),
    [canDelete, canManage, handleDelete, isUploading, openModal]
  );

  return (
    <LearningAssetPage<AiccCanDoStatement>
      title={CAN_DO_STATEMENT_TITLE}
      headerExtra={<AgenticAssetLink />}
      fieldGuideIntro={CAN_DO_STATEMENT_INTRO}
      columns={columns}
      state={state}
      isLocked={isUploading}
      toolbarActions={
        isEditable && (
          <Space size={12}>
            {canManage && (
              <Button type="primary" shape="round" disabled={isActionDisabled} onClick={() => openModal()}>
                {`+ Add ${CAN_DO_STATEMENT_TITLE}`}
              </Button>
            )}
            <Upload accept={FILE_ACCEPT} showUploadList={false} beforeUpload={selectFile} disabled={isActionDisabled}>
              <Button type="primary" shape="round" loading={isValidating} disabled={isActionDisabled}>
                Upload
              </Button>
            </Upload>
          </Space>
        )
      }
    >
      <CanDoStatementModal
        visible={isModalVisible}
        statement={editingItem}
        isSaving={isSaving}
        onCancel={closeModal}
        onSubmit={handleSubmit}
      />
      <UploadPreviewModal<AiccCanDoStatementUploadRow>
        visible={!isNil(preview)}
        fileName={uploadFile?.name}
        preview={preview}
        rows={previewRows}
        columns={previewColumns}
        rowKey={({ id, fluencyLevel, statement }) => id ?? `${fluencyLevel}-${statement}`}
        summary={`${size(
          previewRows
        )} statements are ready to upload. Uploading replaces all current content for this asset.`}
        isSaving={isSaving}
        onCancel={closeUpload}
        onConfirm={confirmUpload}
      />
    </LearningAssetPage>
  );
}

export default CanDoStatementManagement;
